'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

type Stage = 'idle' | 'waiting' | 'ready' | 'result' | 'toosoon';

const TOTAL_ROUNDS = 5;
const MIN_DELAY_MS = 1200;
const MAX_DELAY_MS = 3800;

export default function BrowserReactionTimeTest() {
  const [stage, setStage] = useState<Stage>('idle');
  const [times, setTimes] = useState<number[]>([]);
  const [lastTime, setLastTime] = useState<number | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const greenAtRef = useRef<number>(0);

  // Clear any pending timer on unmount so a green flip never fires after teardown.
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null && typeof window !== 'undefined') {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const round = times.length;
  const finished = round >= TOTAL_ROUNDS;

  function scheduleGreen() {
    if (typeof window === 'undefined') {
      return;
    }
    const delay = MIN_DELAY_MS + Math.random() * (MAX_DELAY_MS - MIN_DELAY_MS);
    timeoutRef.current = window.setTimeout(() => {
      greenAtRef.current = Date.now();
      setStage('ready');
    }, delay);
  }

  function startRound() {
    if (timeoutRef.current !== null && typeof window !== 'undefined') {
      window.clearTimeout(timeoutRef.current);
    }
    setLastTime(null);
    setStage('waiting');
    scheduleGreen();
  }

  function handleBoxClick() {
    if (stage === 'idle' || stage === 'result' || stage === 'toosoon') {
      if (finished && stage !== 'idle') {
        return;
      }
      startRound();
      return;
    }
    if (stage === 'waiting') {
      // Clicked before green — too soon.
      if (timeoutRef.current !== null && typeof window !== 'undefined') {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setStage('toosoon');
      return;
    }
    if (stage === 'ready') {
      const reaction = Date.now() - greenAtRef.current;
      setLastTime(reaction);
      setTimes((prev) => [...prev, reaction]);
      setStage('result');
    }
  }

  function reset() {
    if (timeoutRef.current !== null && typeof window !== 'undefined') {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setTimes([]);
    setLastTime(null);
    setStage('idle');
  }

  const stats = useMemo(() => {
    if (times.length === 0) {
      return { average: null as number | null, best: null as number | null };
    }
    const sum = times.reduce((total, value) => total + value, 0);
    return {
      average: Math.round(sum / times.length),
      best: Math.min(...times),
    };
  }, [times]);

  const box = (() => {
    switch (stage) {
      case 'idle':
        return {
          background: '#3b5bdb',
          text: `Click to start. ${TOTAL_ROUNDS} rounds.`,
          hint: 'When the box turns green, click as fast as you can.',
        };
      case 'waiting':
        return {
          background: '#b02a37',
          text: 'Wait for green…',
          hint: 'Do not click yet.',
        };
      case 'ready':
        return {
          background: '#2f9e44',
          text: 'Click now!',
          hint: '',
        };
      case 'toosoon':
        return {
          background: '#e8590c',
          text: 'Too soon! Click to try this round again.',
          hint: 'Wait for the green box before clicking.',
        };
      case 'result':
      default:
        return {
          background: '#3b5bdb',
          text:
            lastTime !== null
              ? finished
                ? `${lastTime} ms — all ${TOTAL_ROUNDS} rounds done!`
                : `${lastTime} ms. Click for the next round.`
              : 'Click to continue.',
          hint: finished ? 'Press Reset to run the test again.' : '',
        };
    }
  })();

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Reaction Time Test</span>
        <h2 className="tool-live-title">Test your reaction time</h2>
        <p className="tool-live-subtitle">
          Click the box to start, wait for it to turn green, then click as fast as you can. Five
          rounds give you an average and a best time. Everything runs in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <button
            aria-label="Reaction test box"
            className="tool-live-field-full"
            disabled={finished && stage === 'result'}
            onClick={handleBoxClick}
            style={{
              background: box.background,
              color: '#ffffff',
              border: 'none',
              borderRadius: '0.85rem',
              minHeight: '13rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              cursor: finished && stage === 'result' ? 'default' : 'pointer',
              textAlign: 'center',
              padding: '1.5rem',
              width: '100%',
            }}
            type="button"
          >
            <span style={{ fontSize: '1.35rem', fontWeight: 600 }}>{box.text}</span>
            {box.hint ? (
              <span style={{ fontSize: '0.95rem', opacity: 0.9 }}>{box.hint}</span>
            ) : null}
          </button>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={reset} type="button">
              Reset
            </button>
          </div>
        </section>

        <section className="tool-live-preview">
          <div className="tool-html-meta-grid">
            <div className="tool-live-field">
              <span>Round</span>
              <input
                readOnly
                type="text"
                value={`${Math.min(round, TOTAL_ROUNDS)} / ${TOTAL_ROUNDS}`}
              />
            </div>
            <div className="tool-live-field">
              <span>Last</span>
              <input readOnly type="text" value={lastTime !== null ? `${lastTime} ms` : '—'} />
            </div>
            <div className="tool-live-field">
              <span>Average</span>
              <input
                readOnly
                type="text"
                value={stats.average !== null ? `${stats.average} ms` : '—'}
              />
            </div>
            <div className="tool-live-field">
              <span>Best</span>
              <input readOnly type="text" value={stats.best !== null ? `${stats.best} ms` : '—'} />
            </div>
          </div>
          {times.length > 0 ? (
            <div className="tool-live-field tool-live-field-full">
              <span>All rounds</span>
              <input readOnly type="text" value={times.map((time) => `${time} ms`).join(', ')} />
            </div>
          ) : null}
          {finished ? (
            <p className="status-message status-success">
              Done! Average {stats.average} ms, best {stats.best} ms across {TOTAL_ROUNDS} rounds.
            </p>
          ) : null}
          <p className="tool-live-panel-note">
            A typical human reaction time is around 200–300 ms. Clicking before the box turns green
            does not count.
          </p>
        </section>
      </div>
    </div>
  );
}
